import { createSelector } from "reselect";
import { schema, denormalize } from "normalizr";
import { differenceInCalendarDays, isValid } from "date-fns";

import {
  AUTH_STATE_LOGIN,
  AUTH_STATE_UNKNOWN,
  AUTH_STATE_LOGOUT,
  SUBSCRIPTION_TYPE_VIP,
  SUBSCRIPTION_TYPE_PACKAGE,
  PAYMENT_PROVIDER_INFO_MAP,
  PAYMENT_PLATFORM_WEB,
  PAYMENT_TYPE_IAP,
  PaymentProviderVip,
  REQUEST_STATE_SUCCEEDED,
} from "../../constants";

import {
  IAP_WITH_CANCELLED,
  IAP_WITHOUT_CANCELLED,
  NOT_IAP_WITH_CANCELLED,
  NOT_IAP_WITHOUT_CANCELLED,
} from "../constants/actionTypes";

import {
  createCurrentEpsInfoSelector,
  createDramaInfoSelector,
  isEpsSelector,
  dramaIdSelector,
} from "./drama";

export const isAuthenticatedSelector = (state) =>
  state.user.loginState !== AUTH_STATE_UNKNOWN;
export const isLoginSelector = (state) =>
  state.user.loginState === AUTH_STATE_LOGIN;
export const isLogoutSelector = (state) =>
  state.user.loginState === AUTH_STATE_LOGOUT;
export const profileSelector = (state) => state.user.profile;
export const isVipSelector = (state) => state.user.profile?.isVip === true;

export const vipSubscriptionSelector = (state) => state.user.vipSubscription;
export const unfulfilledsSubscriptionSelector = (state) =>
  state.user.unfulfilledsSubscription;

const packageSubscriptionIdsSelector = (state) =>
  state.user.packageSubscriptions;
export const packageSubscriptionEntitiesSelector = (state) =>
  state.entities.packageSubscription;
export const subscribedPackageEntitiesSelector = (state) =>
  state.entities.subscribedPackages;
const dealEntitiesSelector = (state) => state.entities.deal;

const entitiesSelector = (state) => state.entities;

export const packageSubscriptionsSelector = createSelector(
  [entitiesSelector, packageSubscriptionIdsSelector],
  (entities, packageSubscriptionIds) => {
    const now = Date.now();
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

    const packageSubscriptionEntity = new schema.Entity("packageSubscription", {
      srcInfo: {
        deal: new schema.Entity("deal"),
      },
    });

    return denormalize(
      packageSubscriptionIds,
      [packageSubscriptionEntity],
      entities
    ).map((subscription) => {
      const deal = subscription.srcInfo.deal;
      const isMaxPeriod =
        deal.maxPeriod > 0 &&
        subscription.srcInfo.periodIndex >= deal.maxPeriod;
      const isTVOD = deal.maxPeriod === 1;
      const willRenew = !Boolean(deal.cancelledAt) && !isMaxPeriod;
      const willSoonExpire =
        !willRenew && subscription.expiredAt - now < sevenDaysInMs;

      return {
        ...subscription,
        isMaxPeriod,
        isTVOD,
        willRenew,
        willSoonExpire,
      };
    });
  }
);

export const expireSoonPackageSubscriptionsSelector = createSelector(
  [packageSubscriptionsSelector],
  (packageSubscriptions) =>
    packageSubscriptions
      .filter(({ willSoonExpire }) => willSoonExpire)
      .map(({ id }) => id)
);

const readExpireSoonPackageSubscriptionsSelector = (state) =>
  state.user.readExpireSoonPackageSubscriptions;

export const hasUnreadExpireSoonPackageSubscriptionsSelector = createSelector(
  [
    expireSoonPackageSubscriptionsSelector,
    readExpireSoonPackageSubscriptionsSelector,
  ],
  (expireSoonPackageSubscriptions, readExpireSoonPackageSubscriptions) => {
    return expireSoonPackageSubscriptions.some(
      (subscription) =>
        !readExpireSoonPackageSubscriptions.includes(subscription)
    );
  }
);

/** 依照目前 episode 找出最後過期的 package 日期 */
export const createLastPackageSubscriptionExpirySelector = () => {
  return createSelector(
    [
      createCurrentEpsInfoSelector(),
      packageSubscriptionsSelector,
      dealEntitiesSelector,
    ],
    (currentEpsInfo, packageSubscriptions) => {
      const packageIds = currentEpsInfo?.packages?.map?.(({ id }) => id);

      if (!packageIds) return "";

      const matchedSubscriptions = packageSubscriptions.filter((subscription) =>
        packageIds.includes(subscription.item.package)
      );

      if (
        matchedSubscriptions.length >= 1 &&
        matchedSubscriptions.every(({ willRenew }) => !willRenew)
      ) {
        return matchedSubscriptions.sort((a, b) => b.expiredAt - a.expiredAt)[0]
          .expiredAt;
      }
    }
  );
};

/* 依照目前的 episode 判斷訂閱中的 package 是否為 tvod */
export const createCurrentPackageSubsctiptionTypeSelector = () => {
  return createSelector(
    [createCurrentEpsInfoSelector(), packageSubscriptionsSelector],
    (currentEpsInfo, packageSubscriptions) => {
      const packageData = currentEpsInfo?.packages?.[0];

      if (!packageData) return {};

      const matched = packageSubscriptions?.find(
        (item) => item.item?.package === packageData.id
      );

      return {
        id: matched?.item?.package,
        isTVOD: matched?.isTVOD,
      };
    }
  );
};

export const productSubscriptionsFetchStateSelector = (state) =>
  state.user.productSubscriptionsFetchState;

/* 補足 VIP 訂閱現行方案所需資訊 for 訂閱管理頁 */
export const currentVipSubscriptionSelector = createSelector(
  [vipSubscriptionSelector],
  (vipSubscription) => {
    if (Object.keys(vipSubscription) < 1) {
      return {};
    }

    const isIAP =
      PAYMENT_PROVIDER_INFO_MAP[vipSubscription.source]?.type ===
      PAYMENT_TYPE_IAP;
    const isCancelled = Boolean(vipSubscription.cancelledAt);
    const isFromUnfullfilled = Boolean(
      vipSubscription.latestStatus === "unfulfilled"
    );

    let willRenew;
    switch (vipSubscription.source) {
      case PaymentProviderVip.PromotionCode:
        willRenew =
          !isFromUnfullfilled && vipSubscription.orderId && !isCancelled;
        break;
      case PaymentProviderVip.GiftShop:
        willRenew = false;
        break;
      default:
        willRenew = !isFromUnfullfilled && !isCancelled;
    }

    return {
      ...vipSubscription,
      isIAP,
      willRenew,
      isCancelled,
      isFromUnfullfilled,
    };
  }
);

/* 擷取 VIP 訂閱現行方案資訊 for 購買頁： 1)是否為 IAP、2)是否為已取消續訂、3)剩餘天數、4) payment source */
export const currentVipSubscriptionTypeSelector = createSelector(
  [currentVipSubscriptionSelector, productSubscriptionsFetchStateSelector],
  (currentVipSubscription, productSubscriptionsFetchState) => {
    if (productSubscriptionsFetchState === REQUEST_STATE_SUCCEEDED) {
      const isAppStore =
        currentVipSubscription.isIAP &&
        currentVipSubscription.source === PaymentProviderVip.AppStore;
      const isGooglePlay =
        currentVipSubscription.isIAP &&
        currentVipSubscription.source === PaymentProviderVip.GooglePlay;
      const type = currentVipSubscription.isIAP
        ? currentVipSubscription.isFromUnfullfilled
          ? IAP_WITH_CANCELLED
          : currentVipSubscription.isCancelled
          ? IAP_WITH_CANCELLED
          : IAP_WITHOUT_CANCELLED
        : currentVipSubscription.isCancelled
        ? NOT_IAP_WITH_CANCELLED
        : NOT_IAP_WITHOUT_CANCELLED;

      return {
        id: currentVipSubscription.product?.id,
        type,
        remainDays: isValid(currentVipSubscription.expiredAt)
          ? Math.ceil(
              (currentVipSubscription.expiredAt - new Date()) / 1000 / 86400
            )
          : undefined, //無條件進位
        isAppStore,
        isGooglePlay,
      };
    }
  }
);

export const unsubscriptionSelector = (state) => state.user.unsubscription;

export const isWebSubscriptionSelector = createSelector(
  [
    unsubscriptionSelector,
    vipSubscriptionSelector,
    packageSubscriptionEntitiesSelector,
  ],
  (unsubscription, vipSubscription, packageSubscriptionEntities) => {
    switch (unsubscription?.type) {
      case SUBSCRIPTION_TYPE_VIP:
        return (
          PAYMENT_PROVIDER_INFO_MAP[vipSubscription?.source]?.platform ===
          PAYMENT_PLATFORM_WEB
        );

      case SUBSCRIPTION_TYPE_PACKAGE: {
        const subscription = packageSubscriptionEntities[unsubscription.id];
        const code = subscription?.srcInfo.order.paymentGateway.code;
        return (
          PAYMENT_PROVIDER_INFO_MAP[code]?.platform === PAYMENT_PLATFORM_WEB
        );
      }

      default:
        return false;
    }
  }
);

export const favoriteDramasSelector = (state) => state.user.favoriteDramas;
export const watchHistorySelector = (state) => state.user.watchHistory;

export const allWatchHistoryByIdSelector = (state) =>
  state.entities.watchHistory;

export const allWatchHistorySelector = createSelector(
  [allWatchHistoryByIdSelector, watchHistorySelector],
  (allWatchHistoryById, watchHistoryIds) =>
    watchHistoryIds.map((watchHistoryId) => allWatchHistoryById[watchHistoryId])
);

export const createCurrentWatchHistorySelector = () =>
  createSelector(
    [allWatchHistoryByIdSelector, dramaIdSelector],
    (allWatchHistoryById, dramaId) => allWatchHistoryById[dramaId]
  );

/** 適用於整個網站的其他廣告版位 */
export const hasAdSelector = createSelector(
  [isAuthenticatedSelector, isVipSelector],
  (isAuthenticated, isVip) => Boolean(isAuthenticated && !isVip)
);

/** 適用於戲劇頁播放器內的廣告：影音廣告、暫停廣告 */
export const createDramaPlayerHasAdSelector = () =>
  createSelector(
    [
      hasAdSelector,
      createCurrentEpsInfoSelector(),
      subscribedPackageEntitiesSelector,
    ],
    (hasAd, currentEpsInfo, subscribedPackageEntities) => {
      const isOwnEps =
        Array.isArray(currentEpsInfo?.packages) &&
        currentEpsInfo.packages.some(({ id }) => subscribedPackageEntities[id]);

      return Boolean(hasAd && !isOwnEps);
    }
  );

/** 適用於戲劇頁的其他廣告版位 */
export const createDramaPageHasAdSelector = () =>
  createSelector(
    [hasAdSelector, createDramaInfoSelector()],
    (hasAd, dramaInfo) => Boolean(hasAd && dramaInfo.adSettingMode !== 0)
  );

const policyIdsSelector = (state) => state.user.policies;

const policyEntitiesSelector = (state) => state.entities.policy;

export const policiesSelector = createSelector(
  [policyIdsSelector, policyEntitiesSelector],
  (policyIds, policyEntities) =>
    policyIds.map((id) => policyEntities[id]).filter(Boolean)
);

export const isPolicyModalOpenSelector = createSelector(
  [policiesSelector],
  (policies) =>
    policies.some((policy) => Boolean(policy.required && !policy.accept))
);
